import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Loader2, UserCircle, Building, Home } from "lucide-react";

const ICON = { Privato: UserCircle, Azienda: Building, Condominio: Home };

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/customers/search`, { params: { q } });
        setResults(data);
        setOpen(true);
      } catch (e) { /* ignore */ } finally { setLoading(false); }
    }, 250);
  }, [q]);

  const pick = (c) => { setOpen(false); setQ(""); setResults([]); navigate(`/clienti/${c.id}`); };

  return (
    <div className="relative hidden max-w-md flex-1 sm:block" ref={boxRef}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q.trim() && setOpen(true)}
        placeholder="Cerca cliente: nome, telefono, CF, P.IVA…"
        className="h-9 pl-9"
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}
      {open && (
        <div className="absolute left-0 right-0 top-11 z-50 overflow-hidden rounded-xl border border-border bg-white shadow-lg" data-testid="global-search-results">
          {results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500">Nessun cliente trovato.</div>
          ) : (
            results.map((c) => {
              const Icon = ICON[c.type] || UserCircle;
              return (
                <button key={c.id} onClick={() => pick(c)} data-testid={`search-result-${c.id}`}
                  className="flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-left last:border-0 hover:bg-brand/5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/10 text-brand"><Icon className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-slate-800">{c.display_name}</span>
                    <span className="block truncate text-xs text-slate-500">
                      {c.type}{c.phone_primary ? ` · ${c.phone_primary}` : ""}{c.comune ? ` · ${c.comune}` : ""}{c.fiscal_code ? ` · ${c.fiscal_code}` : ""}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
